import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * OAuth Callback Handler
 * 
 * This component handles the callback from Emergent Auth (Google OAuth).
 * It processes the session_id from the URL fragment, exchanges it for a JWT token,
 * and redirects the user to their library.
 * 
 * CRITICAL: This uses useRef for the processed flag to handle React StrictMode
 * and prevent double-processing of the session_id.
 */
export const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { handleGoogleCallback, enableRememberDevice } = useAuth();
    const hasProcessed = useRef(false);

    useEffect(() => {
        // Prevent double-processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processCallback = async () => {
            // Get session_id from URL fragment (after #)
            const hash = location.hash;
            const sessionIdMatch = hash.match(/session_id=([^&]+)/);
            
            if (!sessionIdMatch) {
                console.error('No session_id found in URL');
                navigate('/login', { 
                    state: { error: 'Authentication failed. Please try again.' },
                    replace: true 
                });
                return;
            }

            const sessionId = sessionIdMatch[1];
            
            try {
                const result = await handleGoogleCallback(sessionId);
                
                if (result.success) {
                    // Check if user wanted to remember this device
                    const shouldRemember = localStorage.getItem('remember_me_pending');
                    if (shouldRemember) {
                        localStorage.removeItem('remember_me_pending');
                        await enableRememberDevice();
                    }
                    
                    // Clear the URL fragment and redirect to library
                    window.history.replaceState(null, '', '/auth/callback');
                    navigate('/my-library', { 
                        replace: true,
                        state: { user: result.user }
                    });
                } else {
                    navigate('/login', { 
                        state: { error: result.error },
                        replace: true 
                    });
                }
            } catch (error) {
                console.error('OAuth callback error:', error);
                navigate('/login', { 
                    state: { error: 'Authentication failed. Please try again.' },
                    replace: true 
                });
            }
        };

        processCallback();
    }, [location.hash, handleGoogleCallback, enableRememberDevice, navigate]);

    return (
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center" data-testid="auth-callback">
            <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto mb-4" />
                <p className="text-muted-foreground">Signing you in...</p>
            </div>
        </div>
    );
};

export default AuthCallback;
